import { useState, useEffect, type FC, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "@store/store";
import ProfileCompletionModal from "@components/common/Modal/ProfileCompletionModal";
import AddSkillsPromptModal from "@components/common/Modal/AddSkillsPromptModal";
import { useStudentSkills } from "@features/Profile/services/StudentSkills";
import { getSignInUrlWithRedirectQuery, persistPostAuthRedirect } from "@utils/postAuthRedirect";

interface AuthGuardProps {
	children: ReactNode;
}

const AuthGuard: FC<AuthGuardProps> = ({ children }) => {
	const { isAuthenticated, user } = useSelector((state: RootState) => state.user);
	const location = useLocation();
	const [showSkillsModal, setShowSkillsModal] = useState(false);
	const [hasCheckedSkills, setHasCheckedSkills] = useState(false);

	// Get isProfileComplete from user state (set from API response)
	const isProfileComplete = user?.isProfileComplete ?? false;

	// Check if user has skills (only when profile is complete)
	const { data: studentSkills = [], isLoading: loadingSkills } = useStudentSkills(
		isAuthenticated && isProfileComplete && !hasCheckedSkills
	);

	// Get profile completion modal state from Redux
	const profileCompletionModalOpen = useSelector(
		(state: RootState) => state.modal.profileCompletionModal.isOpen
	);

	// Check if skills exist when profile is complete
	useEffect(() => {
		if (isProfileComplete && isAuthenticated && !loadingSkills && !hasCheckedSkills) {
			setHasCheckedSkills(true);
			// Show skills modal if no skills exist and user just completed profile
			if (studentSkills.length === 0) {
				// Check if this is a fresh profile completion (check localStorage flag)
				const shouldShowSkillsPrompt = !localStorage.getItem("skillsPromptShown");
				if (shouldShowSkillsPrompt) {
					setShowSkillsModal(true);
					localStorage.setItem("skillsPromptShown", "true");
				}
			}
		}
	}, [isProfileComplete, isAuthenticated, loadingSkills, studentSkills.length, hasCheckedSkills]);

	if (!isAuthenticated) {
		const raw = `${location.pathname}${location.search}${location.hash}`;
		persistPostAuthRedirect(raw);
		// Put `redirect` on the sign-in URL so it survives every guest `navigate()` that preserves `location.search`.
		return <Navigate to={getSignInUrlWithRedirectQuery(raw)} state={{ from: location }} replace />;
	}

	return (
		<>
			{/* Render children (dashboard/content) behind the modal */}
			{children}
			{/* Show modal overlay when profile is not complete and modal is opened from Redux */}
			{isAuthenticated && !isProfileComplete && profileCompletionModalOpen && (
				<ProfileCompletionModal isOpen={true} />
			)}
			{/* Show skills prompt modal when profile is complete but no skills */}
			{isAuthenticated && isProfileComplete && showSkillsModal && (
				<AddSkillsPromptModal
					isOpen={showSkillsModal}
					onClose={() => setShowSkillsModal(false)}
				/>
			)}
		</>
	);
};

export default AuthGuard;
